<template>
  <div data-page-name="data-backup-storage">
    <section class="hero">
      <div class="wrapper">
        <div class="left">
          <h1 v-html="$t('data-backup-storage.hero.title')" />
          <p v-html="$t('data-backup-storage.hero.description')" />

          <div class="buttons">
            <Buttons.Link :to="REQUEST_DEMO_URL">
              {{ $t('Get_a_Demo') }}
              <SvgIcon name="arrow-forward" />
            </Buttons.Link>

            <Buttons.Regular
              fill="outline"
              accent="orange"
              @click="setFormState('visible', true)"
            >
              {{ $t('Download_White_Paper') }}
              <SvgIcon name="arrow-forward" />
            </Buttons.Regular>
          </div>
        </div>

        <div class="right">
          <NuxtImg
            src="/redesign/images/server/server-without-ootbi-logo-front.webp"
            alt="Object first server top and front view"
            width="496"
            height="127"
            sizes="xs:325px md:496px"
            densities="x1 x2"
            format="webp"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <section class="purple-short">
      <div class="wrapper">
        <div class="content">
          <h2 v-html="$t('data-backup-storage.purple-1.title')" />
          <p v-html="$t('data-backup-storage.purple-1.p')" />
        </div>
      </div>
    </section>

    <section class="tabs">
      <div class="wrapper">
        <TabList v-slot="{ tab, index, active }" :tabs="TABS" :active-tab-index>
          <button
            v-html="tab"
            :class="{ active }"
            @click="setActiveTabIndex(index)"
          />
        </TabList>

        <ul class="contents">
          <template v-for="(content, index) of TABS_CONTENT" :key="index">
            <li
              v-if="activeTabIndex === index"
              v-html="content"
              class="tab-content"
            />
          </template>
        </ul>
      </div>
    </section>

    <section class="purple-whitepaper bg-purple-gradient">
      <div class="wrapper">
        <div class="left">
          <h2 v-html="$t('data-backup-storage.purple-whitepaper.text')" />

          <Buttons.Regular
            fill="solid"
            accent="orange"
            @click="setFormState('visible', true)"
          >
            {{ $t('Download_White_Paper') }}
            <SvgIcon name="download" fill="#fff" />
          </Buttons.Regular>
        </div>

        <div class="right">
          <NuxtImg
            src="/redesign/images/whitepaper/3-reasons-ootbi-is-the-best-storage-for-veeam-whitepaper-shadow-large.webp"
            alt="A brief description of the benefits of a secure, simple and powerful data warehouse for Veeam users."
            width="656"
            height="643"
            sizes="md:327px xl:261px"
            densities="x1 x2"
            format="webp"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <section class="types-of-backup-storage">
      <div class="wrapper">
        <h2>{{ $t('data-backup-storage.use-cases.title') }}</h2>

        <ExpandableList :items="USE_CASES" open-multiple-items>
          <template #heading="{ item }">
            <strong class="heading">{{ item.heading }}</strong>
          </template>

          <template #content="{ item }">
            <div v-html="item.content" class="content" />
          </template>
        </ExpandableList>
      </div>
    </section>

    <section class="benefits">
      <div class="wrapper">
        <h2 v-html="$t('data-backup-storage.benefits.title')" />

        <ul class="cards">
          <li
            v-for="({ title, icon, description }, index) of CARDS"
            :key="index"
            class="card"
          >
            <strong v-html="title" />
            <component :is="icon" />
            <p v-html="description" />
          </li>
        </ul>
      </div>
    </section>

    <section class="backup-storage-for-everyone">
      <div class="wrapper">
        <div class="heading">
          <h3
            v-html="$t('data-backup-storage.backup-storage-for-everyone.h3')"
          />
          <p
            v-html="$t('data-backup-storage.backup-storage-for-everyone.p-1')"
          />
          <p
            v-html="$t('data-backup-storage.backup-storage-for-everyone.p-2')"
          />
        </div>

        <div class="body">
          <figure>
            <figcaption>
              <header>
                <h4
                  v-html="
                    $t('object-storage.immutability-for-everyone.figure.h4')
                  "
                />
                <strong
                  v-html="
                    $t('object-storage.immutability-for-everyone.figure.strong')
                  "
                />
              </header>

              <NuxtImg
                v-photo-swipe="{
                  caption: isTouchDevice
                    ? $t('ArticleMarkup.zoom-caption-pinch')
                    : $t('ArticleMarkup.zoom-caption-scroll', {
                        icon: SvgZoomIn,
                      }),
                }"
                :alt="$t('object-storage.immutability-for-everyone.figure.alt')"
                src="/redesign/images/SOBR-capacity-breakdown.webp"
                sizes="sm:704px md:820px"
                densities="x1 x2"
              />

              <footer>
                <p
                  v-html="
                    $t('object-storage.immutability-for-everyone.figure.p')
                  "
                />
              </footer>
            </figcaption>
          </figure>

          <ul>
            <Cards.IconBullets
              v-for="(item, key) in WHATS_NEW"
              :key="key"
              v-bind="item"
            />
          </ul>
        </div>
      </div>
    </section>

    <section class="best-storage">
      <div class="wrapper">
        <div class="left">
          <h2 v-html="$t('data-backup-storage.best-storage.title')" />
          <p v-html="$t('data-backup-storage.best-storage.description')" />

          <Buttons.Link :to="REQUEST_DEMO_URL">
            {{ $t('Request_a_Demo') }}
            <SvgIcon name="arrow-forward" />
          </Buttons.Link>
        </div>

        <div class="right">
          <NuxtImg
            src="/redesign/images/server/server-perspective-without-front-panel-square.webp"
            alt="perspective view of the server with the front panel removed"
            width="412"
            height="368"
            sizes="xs:328px md:412px"
            densities="x1 x2"
            format="webp"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <section class="related-topics">
      <div class="wrapper">
        <h2>{{ $t('Related_topics') }}</h2>

        <ul class="list">
          <li v-for="post of relatedTopics" :key="post.slug" class="item">
            <SvgIcon name="check-in-circle-fill" />

            <NuxtLink
              :to="localePathWithSlash(getArticleLink(post))"
              target="_blank"
            >
              {{ post.data.title }}
            </NuxtLink>
          </li>
        </ul>
      </div>
    </section>

    <section class="faq">
      <div class="wrapper">
        <h2 v-html="$t('FAQ')" />

        <ExpandableList :items="FAQ" open-multiple-items>
          <template #heading="{ item }">
            <strong class="heading">{{ item.heading }}</strong>
          </template>

          <template #content="{ item }">
            <p v-html="item.content" class="content" />
          </template>
        </ExpandableList>
      </div>
    </section>
  </div>

  <ClientOnly>
    <Teleport to="#overlay">
      <Transition name="scale-fade">
        <Forms.Wrapper
          v-if="formState.visible"
          can-close
          @close="setFormState('visible', false)"
        >
          <template #title>
            {{
              formState.submitted
                ? $t('Download_Asset')
                : $t('Provide_your_details_to_get_the_asset')
            }}
          </template>

          <transition name="fade" mode="out-in">
            <Modals.DownloadAsset v-if="formState.submitted" />

            <Forms.GetAsset
              v-else
              @loading="setLoading"
              @submitted="setFormState('submitted', true)"
            />
          </transition>
        </Forms.Wrapper>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
// components
import Cards from '~/components/redesign/Cards';
import Forms from '~/components/redesign/Forms';
import Modals from '~/components/redesign/Modals';
import Buttons from '~/components/redesign/Buttons';
import TabList from '~/components/redesign/TabList.vue';

// composables
import useApiSeo from '~/composables/useApiSeo';

// stores
import useUIStore from '~/stores/ui';

// composables
import { useI18n } from '#i18n';
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';

// utils
import get from 'lodash.get';
import { getArticleLink } from '~/utils/getArticleLink';
import buildUTM from '~/utils/buildUTM';

// constants
import { PATHS } from '~/constants';

// directives
import vPhotoSwipe from '~/directives/photoswipe';

// icons
import SvgLockColorfull from '~/assets/svg/lock-colorfull.svg?component';
import SvgLikeColorfull from '~/assets/svg/like-colorfull.svg?component';
import SvgScalabilityColorfull from '~/assets/svg/scalability-colorfull.svg?component';
import SvgUpToDateColorfull from '~/assets/svg/up-to-date-colorfull.svg?component';
import SvgHeartColorfull from '~/assets/svg/heart-colorfull.svg?component';
import SvgRocketColorfull from '~/assets/svg/rocket-colorfull.svg?component';

import SvgZoomIn from '~/assets/svg/zoom-in.svg?raw';

defineOptions({
  components: {
    SvgLockColorfull,
    SvgLikeColorfull,
    SvgScalabilityColorfull,
    SvgUpToDateColorfull,
    SvgHeartColorfull,
    SvgRocketColorfull,
  },
});

const { t } = useI18n();
const { setOverlay, setLoading } = useUIStore();
const localePathWithSlash = useLocalePathWithSlash();

const REQUEST_DEMO_URL =
  localePathWithSlash(PATHS.REQUEST_DEMO.path) +
  buildUTM({
    source: 'guide',
    campaign: 'custom-guide',
    content: 'data-backup-storage',
  });

const isTouchDevice = computed(() => {
  if (import.meta.server) return false;
  return 'ontouchstart' in document;
});

const formState = reactive({ visible: false, submitted: false });
const setFormState = <K extends keyof typeof formState>(
  key: K,
  value: (typeof formState)[K],
) => {
  formState[key] = value;
};

watchEffect(() => {
  setOverlay({ visible: formState.visible });
});

const activeTabIndex = ref(0);
const setActiveTabIndex = (index: number) => {
  activeTabIndex.value = index;
};

const TABS = [
  t('data-backup-storage.tabs-1.title'),
  t('data-backup-storage.tabs-2.title'),
  t('data-backup-storage.tabs-3.title'),
];

const TABS_CONTENT = [
  t('data-backup-storage.tabs-1.content'),
  t('data-backup-storage.tabs-2.content'),
  t('data-backup-storage.tabs-3.content'),
];

const USE_CASES = [
  {
    heading: t('data-backup-storage.use-cases.item-1.title'),
    content: t('data-backup-storage.use-cases.item-1.description'),
  },
  {
    heading: t('data-backup-storage.use-cases.item-2.title'),
    content: t('data-backup-storage.use-cases.item-2.description'),
  },
  {
    heading: t('data-backup-storage.use-cases.item-3.title'),
    content: t('data-backup-storage.use-cases.item-3.description'),
  },
];

const CARDS = [
  {
    title: t('data-backup-storage.benefits.cards-1.title'),
    icon: 'SvgLockColorfull',
    description: t('data-backup-storage.benefits.cards-1.description'),
  },
  {
    title: t('data-backup-storage.benefits.cards-2.title'),
    icon: 'SvgLikeColorfull',
    description: t('data-backup-storage.benefits.cards-2.description'),
  },
  {
    title: t('data-backup-storage.benefits.cards-3.title'),
    icon: 'SvgScalabilityColorfull',
    description: t('data-backup-storage.benefits.cards-3.description'),
  },
  {
    title: t('data-backup-storage.benefits.cards-4.title'),
    icon: 'SvgUpToDateColorfull',
    description: t('data-backup-storage.benefits.cards-4.description'),
  },
  {
    title: t('data-backup-storage.benefits.cards-5.title'),
    icon: 'SvgHeartColorfull',
    description: t('data-backup-storage.benefits.cards-5.description'),
  },
  {
    title: t('data-backup-storage.benefits.cards-6.title'),
    icon: 'SvgRocketColorfull',
    description: t('data-backup-storage.benefits.cards-6.description'),
  },
];

const WHATS_NEW = [
  {
    title: t('object-storage.immutability-for-everyone.card-1.title'),
    icon: 'file-filled-black',
    bullets: [
      t('object-storage.immutability-for-everyone.card-1.bullet-1'),
      t('object-storage.immutability-for-everyone.card-1.bullet-2'),
      t('object-storage.immutability-for-everyone.card-1.bullet-3'),
    ],
  },
  {
    title: t('object-storage.immutability-for-everyone.card-2.title'),
    icon: 'rocket-colorfull',
    bullets: [
      t('object-storage.immutability-for-everyone.card-2.bullet-1'),
      t('object-storage.immutability-for-everyone.card-2.bullet-2'),
      t('object-storage.immutability-for-everyone.card-2.bullet-3'),
    ],
  },
  {
    title: t('object-storage.immutability-for-everyone.card-3.title'),
    icon: 'diamond-colorfull',
    bullets: [t('object-storage.immutability-for-everyone.card-3.bullet-1')],
  },
];

const FAQ = [
  {
    heading: t('data-backup-storage.faq.item-1.title'),
    content: t('data-backup-storage.faq.item-1.content'),
  },
  {
    heading: t('data-backup-storage.faq.item-2.title'),
    content: t('data-backup-storage.faq.item-2.content'),
  },
];

const { data } = await useAsyncGql('RelatedGuides');
const relatedTopics = computed(() => get(data.value, 'blog_articles.data', []));

useApiSeo('data-backup-storage');
</script>

<style lang="scss" scoped>
@use '_/mixins/component';
@use '_/mixins/size';
@use '_/mixins/typo';
@use '_/mixins/flex';
@use '_/mixins/media';
@use '_/variables/shadow';
@use '_/variables/font';
@use '_/variables/color';
@use '_/mixins/text-transform';
@use '_/fn';

@mixin link {
  color: fn.token('link');
  text-decoration-line: underline;

  &:hover {
    background-color: rgb(fn.token('link'), 16%);
  }
}

@mixin veeam {
  display: inline-block;
  background-color: #00b336;
  color: fn.token('text-reversed');
}

@mixin accordion {
  width: 100%;

  strong.heading {
    @include typo.subtitle-1;

    cursor: pointer;
  }

  :deep(.content) {
    @include typo.body-2;
    padding-bottom: 1.5rem;

    p:first-of-type {
      margin-bottom: 0.4rem;
    }
  }
}

[data-page-name='data-backup-storage'] {
  padding-top: 8rem;

  @include media.tablet {
    padding-top: 4rem;
  }

  @include media.mobile {
    padding-top: 1rem;
  }

  section.hero {
    margin-bottom: 5rem;

    @include media.tablet {
      margin-bottom: 4.5rem;
    }

    @include media.mobile {
      margin-bottom: 1rem;
    }

    .wrapper {
      padding: 0 2rem;

      @include size.wrapper(80rem);
      @include flex.center-sb;

      gap: 0 2rem;

      @include media.tablet {
        @include flex.start-center;
        flex-direction: column;
      }

      @include media.mobile {
        padding: 1rem;
      }

      .left {
        @include flex.start-start;

        flex-direction: column;

        @include media.tablet {
          margin-bottom: 3rem;
        }

        @include media.mobile {
          margin-bottom: 2.4rem;
          width: 100%;
        }

        h1 {
          @include typo.h2;

          margin-bottom: 2rem;

          // @include size.fluid-max-width(35rem);

          @include media.tablet {
            margin-bottom: 1.5rem;

            // @include size.fluid-max-width(100%);
          }

          @include media.mobile {
            @include typo.h2;

            margin-bottom: 1.6rem;
          }
        }

        p {
          @include typo.body-1;

          @include media.tablet {
            margin-bottom: 0.5rem;
          }

          @include media.mobile {
            margin-bottom: 0.5rem;
          }
        }

        .buttons {
          @include flex.center-sb;

          gap: 0 2rem;
          margin-top: 2rem;

          @include media.tablet {
            margin-top: 1rem;
            gap: 1.5rem;
          }

          @include media.mobile {
            flex-direction: column;
            width: 100%;
          }

          [data-component-name='Buttons.Link'],
          [data-component-name='Buttons.Regular'] {
            @include media.mobile {
              width: 100%;
            }
          }

          [data-component-name='Buttons.Regular'] {
            svg {
              fill: fn.token('icon-primary');
            }
          }
        }
      }

      .right {
        @include flex.center-center;

        @include media.tablet {
          width: 100%;
        }

        img {
          width: 100%;
          margin-inline: 3rem;

          @include media.tablet {
            margin-inline: auto;
            max-width: 31rem;
          }

          @include media.mobile {
            height: auto;
          }
        }
      }
    }
  }

  section.purple-short {
    margin-bottom: 8rem;

    @include media.mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem;

      @include size.wrapper(80rem);

      @include media.tablet {
        padding: 0 2rem;
      }

      @include media.mobile {
        padding: 0;
        margin: 0;
        width: 100%;
      }

      .content {
        padding: 2rem;
        background-color: fn.token('surf-cont-secon-action');

        @include media.tablet {
          padding: 1.5rem;
        }

        @include media.mobile {
          padding: 1.5rem 1rem;
        }

        h2 {
          @include typo.h4;

          color: fn.token('text-reversed');
          padding-bottom: 1rem;
        }

        &:deep(p) {
          color: fn.token('text-reversed');

          @include typo.body-3;

          &:not(:last-of-type) {
            margin-bottom: 0.5rem;
          }

          a {
            color: fn.token('link-reversed');
            text-decoration: underline;

            &:hover {
              background-color: rgb(fn.token('link-reversed'), 16%);
            }
          }

          strong {
            font-weight: 400;
          }
        }
      }
    }
  }

  section.tabs {
    margin-bottom: 6rem;

    @include media.tablet {
      margin-bottom: 7rem;
    }

    @include media.mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem 1rem;

      @include size.wrapper(80rem);

      @include media.mobile {
        padding: 0 1rem;
      }

      .intro {
        margin-bottom: 5rem;

        @include media.tablet {
          margin-bottom: 2.4rem;
        }

        @include media.mobile {
          margin-bottom: 1.5rem;
        }

        h2 {
          @include typo.h3;

          margin-bottom: 2rem;

          @include media.mobile {
            margin-bottom: 1.5rem;
          }
        }

        :deep(p) {
          @include typo.body-3;

          &:not(:last-of-type) {
            margin-bottom: 0.5rem;
          }

          a {
            @include link;
          }
        }
      }

      [data-component-name='TabList'] {
        button {
          height: 100%;
          width: 100%;
          padding: 1rem 0;

          @include typo.subtitle-3;

          color: fn.token('text-primary');
          transition: color 0.15s ease;

          @include text-transform.hyphen-word-break;

          &.active {
            color: fn.token('text-action');
          }
        }
      }

      ul.contents {
        &:deep(li.tab-content) {
          margin: 5rem auto 1.5rem;

          @include size.fluid-max-width(50rem);

          @include media.tablet {
            margin: 2.5rem auto 0;
          }

          @include media.mobile {
            margin: 1.5rem auto 2.5rem;
          }

          p {
            @include typo.body-3;

            &:not(:last-of-type) {
              margin-bottom: 0.5rem;
            }
          }

          ol {
            li {
              margin-bottom: 1rem;

              p {
                @include typo.body-3;

                strong {
                  font-weight: 600;
                }
              }

              &::marker {
                @include typo.body-3;
                font-weight: 600;
              }
            }
          }
        }
      }
    }
  }

  section.purple-whitepaper {
    margin-bottom: 8rem;

    @include media.mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 8rem 2rem;
      @include size.wrapper(80rem);

      @include flex.start-sb;
      gap: 0 2rem;

      @include media.tablet {
        padding: 5rem 2rem;
      }

      @include media.mobile {
        @include flex.center-sb;
        flex-direction: column;
        padding: 2.5rem 1rem 2.5rem 1rem;
      }

      .left {
        @include flex.start-center;
        flex-direction: column;
        align-self: center;
        max-width: 50%;

        @include media.mobile {
          width: 100%;
          max-width: 100%;
        }

        &:deep(h2) {
          @include typo.h3;
          color: fn.token('text-reversed');
          margin: 0.25rem 0 2rem;

          @include media.tablet {
            margin: 0.2rem 0 1.5rem 0;
          }
        }

        [data-component-name='Buttons.Regular'] {
          @include media.mobile {
            width: 100%;
          }
        }
      }

      .right {
        width: 50%;

        @include media.mobile {
          display: none;
        }

        img {
          width: 35rem;
          height: auto;

          margin-left: 2.125rem;

          @include media.tablet {
            min-width: 20.4375rem;
            margin-left: 0;
            display: block;
            width: 100%;
            height: auto;
          }
        }
      }
    }
  }

  section.types-of-backup-storage {
    margin-bottom: 6.3rem;

    @include media.mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem;

      @include size.wrapper(80rem);
      @include flex.start-start;
      flex-direction: column;

      @include media.tablet {
      }

      @include media.mobile {
        padding: 0 1rem;
      }

      h2 {
        @include typo.h3;
        margin-bottom: 5rem;

        @include media.tablet {
          margin-bottom: 2.5rem;
        }

        @include media.mobile {
          margin-bottom: 2rem;
        }
      }

      p {
        @include typo.body-3;
      }

      [data-component-name='ExpandableList'] {
        @include accordion;
      }
    }
  }

  section.benefits {
    margin-bottom: 6.5rem;

    @include media.tablet {
      margin-bottom: 6rem;
    }

    @include media.mobile {
      margin-bottom: 4rem;
    }

    @mixin card {
      @include flex.start-start;

      flex-direction: column;
      padding: 2rem;
      box-shadow: shadow.$elevation-1;

      @include media.tablet {
        padding: 1.5rem;
      }

      @include media.mobile {
        padding: 1.5rem 1rem;
      }

      strong {
        @include typo.subtitle-1;
      }

      svg {
        @include size.fixed(3.125rem);

        margin: 1.5rem 0;
      }

      &:deep(p) {
        @include typo.body-2;

        a {
          color: fn.token('link');
          text-decoration: underline;

          &:hover {
            background-color: rgb(fn.token('link'), 16%);
          }
        }
      }
    }

    .wrapper {
      padding: 0 2rem;

      @include size.wrapper(80rem);

      @include media.mobile {
        padding: 0 1rem;
      }

      h2 {
        @include typo.h3;

        margin-bottom: 5rem;

        @include media.tablet {
          max-width: 100%;
        }

        @include media.mobile {
          margin-bottom: 1.5rem;
        }
      }

      &::v-deep(p) {
        @include typo.body-3;
        @include size.fluid-max-width(57rem);

        @include media.mobile {
          margin-bottom: 1rem;
        }

        a {
          color: fn.token('link');
          text-decoration: underline;

          &:hover {
            background-color: rgb(fn.token('link'), 16%);
          }
        }
      }

      h4 {
        @include typo.h4;

        margin: 5rem 0 2rem;

        @include media.tablet {
          margin-bottom: 2.5rem;
        }

        @include media.tablet {
          margin-bottom: 0;
        }
      }

      ul.cards {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: auto;
        grid-gap: 2rem;
        margin-top: 2rem;

        @include media.tablet {
          grid-template-columns: repeat(2, 1fr);
        }

        @include media.mobile {
          grid-gap: 1rem;
          grid-template-columns: 1fr;
        }

        li.card {
          @include card;

          @include media.mobile {
            padding: 1.5rem 1rem 0.5rem;
          }
        }
      }
    }
  }

  section.backup-storage-for-everyone {
    margin-bottom: 2rem;

    @include media.mobile {
      margin-bottom: 0;
    }

    .wrapper {
      @include size.wrapper(80rem);

      padding: 2rem;

      @include flex.start-start;
      flex-direction: column;

      @include media.mobile {
        padding: 1rem;
      }

      .heading {
        @include size.fluid-max-width(70rem);
        margin-bottom: 4rem;

        @include media.tablet {
          margin-bottom: 2rem;
        }

        @include media.mobile {
          margin-bottom: 0;
        }

        h3 {
          @include typo.h3;
          margin-bottom: 2rem;
        }

        p {
          @include typo.body-2;

          &:first-of-type {
            margin-bottom: 0.4rem;
          }
        }
      }

      .body {
        @include flex.start-start;
        flex-direction: column-reverse;

        @include media.tablet {
          flex-direction: column;
        }

        figure {
          @include size.wrapper(50.0313rem);

          margin: 4.5rem auto;

          @include media.tablet {
            margin: 3rem auto 4rem;
          }

          @include media.mobile {
            margin: 1.5rem auto;
          }

          figcaption {
            h4 {
              @include typo.h3;

              margin-bottom: 1rem;

              @include media.mobile {
                margin-bottom: 0.5rem;
              }

              :deep(span) {
                background-color: color.$green;
                color: fn.token('text-reversed');
                padding-inline: 0.5rem;
              }
            }

            :deep(strong) {
              @include typo.h4;

              sup {
                font-size: 1.125rem;

                @include media.mobile {
                  font-size: 0.5rem;
                }
              }
            }

            img {
              width: 100%;
              height: auto;
            }

            :deep(p) {
              @include typo.body-4;

              sup {
                font-size: 0.625rem;
              }
            }
          }
        }

        ul {
          margin-bottom: 3rem;

          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;

          @include media.mobile {
            grid-template-columns: 1fr;
            gap: 0;
          }
        }
      }
    }
  }

  section.best-storage {
    background-color: fn.token('surf-cont-secondary');

    margin-bottom: 8rem;

    @include media.mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem;

      @include size.wrapper(80rem);
      @include flex.center-sb;
      gap: 2rem;

      @include media.tablet {
        padding: 4rem 2rem;
        gap: 2rem 0;
        flex-direction: column;
      }

      @include media.mobile {
        padding: 2.5rem 1.5rem;
      }

      .left {
        @include size.fluid-max-width(47.75rem);
        @include flex.start-start;

        flex-direction: column;
        gap: 1rem 2rem;

        @include media.tablet {
          @include size.fixed-width(100%);

          gap: 1.5rem 0;
        }

        h2 {
          @include typo.h4;

          &:deep(.veeam) {
            position: relative;

            @include veeam;

            line-height: 3rem;

            @include media.tablet {
              line-height: 2.5rem;
            }

            @include media.mobile {
              line-height: 1.75rem;
            }
          }
        }

        &:deep(p) {
          @include typo.body-2;

          a {
            @include typo.link-text-2;
            @include component.app-link;
          }
        }

        [data-component-name='Buttons.Link'] {
          display: block;
          width: max-content;

          @include media.mobile {
            width: 100%;
          }
        }
      }

      .right {
        img {
          display: block;
          margin: 2rem 0;

          @include media.tablet {
            width: 100%;
            max-width: 37rem;
            margin: 0 auto;
          }

          @include media.mobile {
            width: 100%;
            max-width: 328px;
            height: auto;
          }
        }
      }
    }
  }

  section.related-topics {
    margin-bottom: 6.5rem;

    @include media.tablet {
      margin-bottom: 8rem;
    }

    @include media.mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem;

      @include size.wrapper(80rem);

      @include media.mobile {
        padding: 0 1rem;
      }

      h2 {
        @include typo.h3;
        margin-bottom: 2.5rem;

        @include media.tablet {
          margin-bottom: 0;
        }

        @include media.mobile {
          margin-bottom: 2.5rem;
        }
      }

      ul.list {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(3, auto);
        grid-auto-flow: column;
        grid-gap: 0 2rem;

        @include media.tablet {
          grid-template-columns: repeat(2, 1fr);
          grid-auto-flow: row;
        }

        @include media.mobile {
          grid-template-columns: repeat(1, 1fr);
        }

        li.item {
          @include flex.start-start;
          gap: 1rem;

          margin-top: 1rem;

          &:nth-child(1),
          &:nth-child(4) {
            margin-top: 0;

            @include media.tablet {
              margin-top: 1rem;
            }
          }

          a {
            @include component.app-link;
          }

          svg {
            @include size.fixed(1.5rem);
            fill: fn.token('surf-cont-prim-action');
          }
        }
      }
    }
  }

  section.faq {
    margin-bottom: 7rem;

    @include media.mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem;

      @include size.wrapper(80rem);
      @include flex.start-start;
      flex-direction: column;

      gap: 4rem;

      @include media.tablet {
        flex-direction: column;
        gap: 3rem 0;
      }

      @include media.mobile {
        padding: 0 1rem;
        gap: 2rem 0;
      }

      h2 {
        @include typo.h3;
      }

      [data-component-name='ExpandableList'] {
        @include accordion;
      }
    }
  }
}
</style>
